import {
	parseCategoryPath,
	SHOP_ALL_SLUG,
	type CategoryPath
} from "./category-path";

// Pages are one size on every device, so a shared `?page=N` shows the same products:
// 17 rows of 3 from lg up. Below lg the same pages are shown 24 at a time.
export const LISTING_PAGE_SIZE = 51;

export const LISTING_STEP_BELOW_LG = 24;

// A `?page=N` url renders at most this many pages ending at N, so a deep link stays light.
export const LISTING_WINDOW_PAGES = 3;

// Algolia returns at most the first 1000 hits of a listing.
const SEARCHABLE_HITS = 1000;

export const MAX_LISTING_PAGES = Math.ceil(SEARCHABLE_HITS / LISTING_PAGE_SIZE);

export const LISTING_PAGE_PARAM = "page";

const DEPTH_SEGMENT = "page";
const WHOLE_NUMBER = /^\d+$/;

/** A category path, and the last page of its listing the url shows. */
export interface ListingPath extends CategoryPath {
	pages: number;
}

/** Reads `?page=` as the last page shown; past the deepest page it clamps to it. */
export function parseListingPages(
	value: string | null | undefined
): number | undefined {
	if (!value || !WHOLE_NUMBER.test(value)) return undefined;
	const pages = Number(value);
	return pages >= 1 ? Math.min(pages, MAX_LISTING_PAGES) : undefined;
}

// `?page=N` is served from `/shop/<super>/<category>/page/<N>` so each depth caches like any page.
export function listsProducts(path: CategoryPath): boolean {
	return Boolean(path.categorySlug) || path.superSlug === SHOP_ALL_SLUG;
}

export function parseListingPath(
	segments: readonly string[] | undefined
): ListingPath | null {
	const depthAt = (segments?.length ?? 0) - 2;
	if (depthAt >= 1 && segments?.[depthAt] === DEPTH_SEGMENT) {
		const path = parseCategoryPath(segments.slice(0, depthAt));
		const count = segments[depthAt + 1];
		const pages = parseListingPages(count) ?? 0;
		const isServedDepth = pages > 1 && String(pages) === count;
		return path && listsProducts(path) && isServedDepth
			? { ...path, pages }
			: null;
	}

	const path = parseCategoryPath(segments);
	return path ? { ...path, pages: 1 } : null;
}

export function toListingDepthPath(pathname: string, pages: number): string {
	return `${pathname}/${DEPTH_SEGMENT}/${pages}`;
}

export function firstWindowPage(lastPage: number): number {
	return Math.max(1, lastPage - LISTING_WINDOW_PAGES + 1);
}
